import { createContext, useContext } from 'react';
import { useStore } from 'zustand';
import type { Project } from '../core/model';
import type { RuntimeState, StudioApi } from '../engine/api';
import type { ProjectStoreState } from '../state/api';
import type { Actions } from './actions';
import type { UiState, UiStore } from './uiStore';

export interface UiContextValue {
  studio: StudioApi;
  ui: UiStore;
  actions: Actions;
}

export const UiContext = createContext<UiContextValue | null>(null);

export function useUiContext(): UiContextValue {
  const value = useContext(UiContext);
  if (!value) throw new Error('Studio UI context is missing');
  return value;
}

export const useStudio = (): StudioApi => useUiContext().studio;
export const useActions = (): Actions => useUiContext().actions;

/** Subscribe to a slice of the project document. Keep selectors narrow. */
export function useProject<T>(selector: (project: Project) => T): T {
  const { studio } = useUiContext();
  return useStore(studio.project, (s: ProjectStoreState) => selector(s.project));
}

export function useProjectStore<T>(selector: (state: ProjectStoreState) => T): T {
  const { studio } = useUiContext();
  return useStore(studio.project, selector);
}

export function useRuntime<T>(selector: (runtime: RuntimeState) => T): T {
  const { studio } = useUiContext();
  return useStore(studio.runtime, selector);
}

export function useUi<T>(selector: (state: UiState) => T): T {
  const { ui } = useUiContext();
  return useStore(ui, selector);
}

/** The scene whose items the Sources panel and the editor operate on. */
export const editedSceneIdOf = (runtime: RuntimeState): string | null =>
  runtime.previewSceneId ?? runtime.programSceneId;

export function useEditedSceneId(): string | null {
  return useRuntime(editedSceneIdOf);
}
